import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import size from 'lodash/size';
import first from 'lodash/first';
import get from 'lodash/get';
import TimeAgoWrapper from 'shared/components/TimeAgoWrapper';

import { Message, Attachment } from 'models';
import * as keyboardEventHelpers from 'globals/keyboardEvents';

import * as chatAppActions from '../../../app/ChatApp/actions';
import * as chatAppSelectors from '../../../app/ChatApp/selectors';
import { staticStyles as styles } from './styles';
import ChatMessageForCustomer from '../ChatMessageForCustomer';
import ChatMessageForUser from '../ChatMessageForUser';
import ChatMessageArticleList from '../ChatMessageArticleList';
import messages from './messages';

export class ChatMessage extends PureComponent {
  constructor(props) {
    super(props);

    this.registerDOMNode = this.registerDOMNode.bind(this);
    this.handleFailedRetry = this.handleFailedRetry.bind(this);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillMount() {
    const { message } = this.props;
    const attachments = message.attachments;
    if (size(attachments)) {
      const attachment = first(attachments);
      this.props.fetchMessageAttachment(message.id, attachment.id);
    }
  }

  registerDOMNode(dom) {
    const { provideDOMNode } = this.props;
    provideDOMNode(dom);
  }

  handleFailedRetryKeyPress = e => {
    const { onRetry, message } = this.props;

    if (keyboardEventHelpers.isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      onRetry(message);
    }
  };

  handleFailedRetry(e) {
    e.preventDefault();
    this.props.onRetry(this.props.message);
  }

  renderFailed() {
    const { message, intl } = this.props;
    if (!message.failed) return null;

    return (
      <div
        tabIndex="0"
        style={styles.failed}
        title={intl.formatMessage(messages.sent_failure)}
        onKeyPress={this.handleFailedRetryKeyPress}
        aria-label={intl.formatMessage(messages.sent_failure)}
        onClick={this.handleFailedRetry}
      />
    );
  }

  renderTimeAgo() {
    const { lastMessageTimeUTC, isLastMessageInSession, message } = this.props;

    if (!isLastMessageInSession || !lastMessageTimeUTC) return null;

    const userType = get(message, 'sentBy.type');

    const sentByUser = userType === 'user';

    return (
      <span style={sentByUser ? styles.userDate : styles.customerDate} aria-live="polite">
        <TimeAgoWrapper date={lastMessageTimeUTC} />
      </span>
    );
  }

  renderBodyForType() {
    const { message, short, attachment, onSelectArticle, onHandleResponse } = this.props;

    const isArticleList = get(message, 'type') === 'articleList';

    if (message.sentBy.type === 'customer') {
      return <ChatMessageForCustomer message={message} attachment={attachment} timeAgoNode={this.renderTimeAgo()} />;
    } else if (isArticleList) {
      return (
        <ChatMessageArticleList
          onSelectArticle={onSelectArticle}
          message={message}
          onHandleResponse={onHandleResponse}
        />
      );
    } else if (message.sentBy.type === 'user') {
      return (
        <ChatMessageForUser
          message={message}
          attachment={attachment}
          hideAvatar={short}
          timeAgoNode={this.renderTimeAgo()}
        />
      );
    }
    return null;
  }

  render() {
    const { short } = this.props;
    const style = Object.assign({}, styles.message);
    if (short) {
      style.marginTop = '5px';
    }

    return (
      <div style={style} ref={this.registerDOMNode}>
        {this.renderBodyForType()}
        {this.renderFailed()}
      </div>
    );
  }
}

ChatMessage.propTypes = {
  attachment: PropTypes.instanceOf(Attachment),
  message: PropTypes.instanceOf(Message),
  short: PropTypes.bool,
  provideDOMNode: PropTypes.func,
  onRetry: PropTypes.func,
  intl: intlShape.isRequired,
  fetchMessageAttachment: PropTypes.func,
  isLastMessageInSession: PropTypes.bool,
  lastMessageTimeUTC: PropTypes.number,
  onSelectArticle: PropTypes.func,
  onHandleResponse: PropTypes.func,
};

ChatMessage.defaultProps = {
  attachment: Attachment(),
  provideDOMNode: () => {},
  onRetry: () => {},
  onHandleResponse: () => {},
  fetchMessageAttachment: () => {},
  message: Message(),
  short: false,
  isLastMessageInSession: false,
  lastMessageTimeUTC: undefined,
};

export const mapStateToProps = (state, props) => {
  const getSessionLastMessageTimeUTC = chatAppSelectors.makeGetSessionLastMessageTimeUTC();
  const selectMessageAttachments = chatAppSelectors.makeSelectMessageAttachment();

  return {
    attachment: selectMessageAttachments(state, props),
    lastMessageTimeUTC: getSessionLastMessageTimeUTC(state, { item: { id: props.message.sessionId } }),
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    fetchMessageAttachment(messageId, attachmentId) {
      if (!messageId && !attachmentId) return;
      dispatch(chatAppActions.fetchMessageAttachment(messageId, attachmentId));
    },
  };
};

export const intlChatMessage = injectIntl(ChatMessage, { withRef: true });

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(intlChatMessage);
